import React, { useState, useEffect, useCallback } from "react";
import { useEmployerJobApi } from "../../../_common/hooks/actions/job/appEmployerJobApiHook";
import { useTranslation } from "src/_common/hooks/useTranslation";
import parse from "html-react-parser";
import { EXTERNAL_URL } from "src/_config";

interface ViewProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobId?: string;
  jobseekerId?: number;
  candidateType?: number; // Add candidate type prop
  user_type?: string; // Add user type prop
}

const ViewProfileModal: React.FC<ViewProfileModalProps> = ({
  isOpen,
  onClose,
  jobId,
  jobseekerId,
  candidateType,
  user_type,
}) => {
  const [loading, setLoading] = useState(false);
  const [candidateDetails, setCandidateDetails] = useState<any>(null);
  const { t } = useTranslation("job-details");
  const jobApi = useEmployerJobApi();

  const getInitials = (name: string) => {
    const parts = name.trim().split(" ").filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return parts[0]?.substring(0, 2).toUpperCase() || "NA";
  };

  const formatDateOfBirth = (dateString: string) => {
    if (!dateString) return "-";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return original if invalid

      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch (error) {
      console.error("Error formatting date of birth:", error);
      return dateString; // Return original if error
    }
  };

  const fetchJobseekerDetails = () => {
    if (!jobseekerId) {
      console.warn("No jobseeker ID provided to fetch details");
      return;
    }

    setLoading(true);
    // Pass bulk_cv=1 if candidate type is 3 or user_type is "bulk-cv"
    const isBulkCV = user_type === "bulk-cv" || (!user_type && candidateType === 3);
    jobApi.callGetJobseekerDetailsById(
      jobseekerId,
      isBulkCV,
      (message: any, response: any) => {
        console.log("Jobseeker details fetched successfully:", response);
        if (response) {
          const jobseeker = response;
          setCandidateDetails(jobseeker);
        }
        setLoading(false);
      },
      (error: any) => {
        console.error("Error fetching jobseeker details:", error);
        setLoading(false);
      },
    );
  };

  const getGenderLabel = (genderId: number) => {
    const genderMap: { [key: number]: string } = {
      1: "Male",
      2: "Female",
      3: "Other",
    };
    return genderMap[genderId] || "-";
  };

  useEffect(() => {
    if (isOpen) {
      if (jobseekerId) {
        fetchJobseekerDetails();
      } else {
        setLoading(false);
      }
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen, jobseekerId]);

  if (!isOpen) return null;

  const fullName =
    candidateDetails?.basic_details?.full_name ||
    (candidateDetails?.basic_details?.first_name && candidateDetails?.basic_details?.last_name
      ? `${candidateDetails.basic_details.first_name} ${candidateDetails.basic_details.last_name}`
      : candidateDetails?.basic_details?.first_name || "");

  const subtitle =
    (candidateDetails?.basic_details?.cv_headline || candidateDetails?.employment_details?.[0]?.designation || "") +
    (candidateDetails?.basic_details?.current_location
      ? (candidateDetails?.basic_details?.cv_headline || candidateDetails?.employment_details?.[0]?.designation
          ? " | "
          : "") + candidateDetails?.basic_details?.current_location
      : "");

  const genderLabel =
    candidateDetails?.resolved_labels?.gender?.name ||
    (candidateDetails?.basic_details?.gender_id ? getGenderLabel(candidateDetails.basic_details.gender_id) : "");

  const workplaceLabel = candidateDetails?.resolved_labels?.workplace_types?.length
    ? candidateDetails.resolved_labels.workplace_types.map((w: any) => w.name).join(", ")
    : "";

  const hasVideo = !!candidateDetails?.document_full_path?.video_file;

  const totalExp = candidateDetails?.total_experience;
  const expLabel = (() => {
    if (!totalExp || totalExp <= 0) return "";
    const years = Math.floor(totalExp);
    const months = Math.round((totalExp - years) * 12);
    const parts: string[] = [];
    if (years > 0) parts.push(`${years} ${years === 1 ? "Year" : "Years"}`);
    if (months > 0) parts.push(`${months} ${months === 1 ? "Month" : "Months"}`);
    return parts.join(", ") || `${totalExp} Years`;
  })();

  return (
    <div className="view-profile-modal-overlay">
      <div className="view-profile-modal">
        {/* Header */}
        <div className="view-profile-header">
          <div className="header-left"></div>
          <div className="header-right">
            <button className="close-btn" onClick={onClose}>
              <i className="fa fa-times"></i>
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="view-profile-content">
          {loading ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <p>{t("jobDetails.viewProfile.loading")}</p>
            </div>
          ) : (
            <>
              {/* Profile Header Section */}
              <div className="profile-header-section">
                {/* Avatar */}
                <div className="profile-avatar-wrapper">
                  {candidateDetails?.document_full_path?.profile_pic ? (
                    <img
                      className="profile-avatar-img"
                      src={candidateDetails.document_full_path.profile_pic}
                      alt={fullName || t("jobDetails.viewProfile.unknownCandidate")}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        const sibling = e.currentTarget.nextElementSibling as HTMLElement;
                        if (sibling) sibling.style.display = "flex";
                      }}
                    />
                  ) : null}
                  <div
                    className="profile-avatar-initials"
                    style={candidateDetails?.document_full_path?.profile_pic ? { display: "none" } : {}}
                  >
                    {fullName ? getInitials(fullName) : "NA"}
                  </div>
                </div>

                {/* Info */}
                <div className="profile-info">
                  <h1 className="profile-name">{fullName}</h1>
                  {subtitle && <p className="profile-title">{subtitle}</p>}

                  {/* Inline tags */}
                  <div className="profile-inline-tags">
                    {expLabel && (
                      <span className="profile-inline-tag">
                        <i className="fa fa-briefcase"></i>
                        {expLabel}
                      </span>
                    )}
                    {genderLabel && genderLabel !== "-" && (
                      <span className="profile-inline-tag">
                        <i className="fa fa-user"></i>
                        {genderLabel}
                      </span>
                    )}
                    {workplaceLabel && (
                      <span className="profile-inline-tag">
                        <i className="fa fa-wifi"></i>
                        {workplaceLabel}
                      </span>
                    )}
                    {hasVideo && (
                      <span
                        className="profile-inline-tag video-tag"
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          const videoFile = candidateDetails.document_full_path.video_file;
                          const videoUrl = videoFile.startsWith("http") ? videoFile : `${jobseekerId}/${videoFile}`;
                          window.open(videoUrl, "_blank");
                        }}
                      >
                        <i className="fa fa-video-camera" style={{ marginRight: "4px" }}></i>
                        {t("jobDetails.viewProfile.videoCv")}
                      </span>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className="profile-actions">
                    {candidateDetails?.jobseeker_public_profiles &&
                      candidateDetails.jobseeker_public_profiles.length > 0 &&
                      candidateDetails.jobseeker_public_profiles[0]?.public_profile_url && (
                        <button
                          className="btn-outline"
                          onClick={() => {
                            const publicProfileUrl = candidateDetails.jobseeker_public_profiles[0].public_profile_url;
                            window.open(`${EXTERNAL_URL}profile/${publicProfileUrl}`, "_blank");
                          }}
                        >
                          <i className="fa fa-user-circle" style={{ marginRight: "4px" }}></i>
                          {t("jobDetails.viewProfile.publicProfile", "Dr.Job Profile")}
                        </button>
                      )}
                    {candidateDetails?.document_full_path?.system_resume_file && (
                      <button
                        className="btn-outline"
                        onClick={() => {
                          const resumeUrl = candidateDetails.document_full_path.system_resume_file.startsWith("http")
                            ? candidateDetails.document_full_path.system_resume_file
                            : candidateDetails.document_full_path.system_resume_file;
                          window.open(resumeUrl, "_blank");
                        }}
                      >
                        <i className="fa fa-download" style={{ marginRight: "4px" }}></i>
                        {t("jobDetails.viewProfile.download")}
                      </button>
                    )}
                    {candidateDetails?.document_full_path?.resume_file && (
                      <button
                        className="btn-outline"
                        onClick={() => {
                          const resumeUrl = candidateDetails.document_full_path.resume_file.startsWith("http")
                            ? candidateDetails.document_full_path.resume_file
                            : candidateDetails.document_full_path.resume_file;
                          window.open(resumeUrl, "_blank");
                        }}
                      >
                        <i className="fa fa-file-text" style={{ marginRight: "4px" }}></i>
                        {t("jobDetails.viewProfile.myResume")}
                      </button>
                    )}
                    <button className="btn-preview" onClick={() => {}}>
                      <i className="far fa-comment-alt"></i>
                      Message
                    </button>
                  </div>
                </div>
              </div>

              {/* Content Sections */}
              <div className="profile-sections">
                {/* Skills Section */}
                {(() => {
                  const skills =
                    candidateDetails?.basic_details?.key_skills ||
                    candidateDetails?.basic_details?.key_skills_custome ||
                    [];

                  let skillsString = "";

                  if (Array.isArray(skills)) {
                    if (skills.length > 0 && skills[0]?.category && Array.isArray(skills[0]?.skills)) {
                      const allSkills = skills.flatMap((category: any) => category.skills || []);
                      skillsString = allSkills.join(", ");
                    } else {
                      skillsString = skills.join(", ");
                    }
                  } else if (typeof skills === "string") {
                    skillsString = skills;
                  }

                  return skillsString ? (
                    <div className="profile-section has-divider">
                      <h3 className="section-title">{t("jobDetails.viewProfile.sections.skills")}</h3>
                      <p className="section-content">{skillsString}</p>
                    </div>
                  ) : null;
                })()}

                {/* Profile Section */}
                {candidateDetails?.basic_details?.profile_summary && (
                  <div className="profile-section has-divider">
                    <h3 className="section-title">{t("jobDetails.viewProfile.sections.profile")}</h3>
                    <div className="section-content">
                      <p>{candidateDetails?.basic_details?.profile_summary}</p>
                    </div>
                  </div>
                )}

                {/* Work Experience Section */}
                {(() => {
                  const employmentDetails = candidateDetails?.employment_details || candidateDetails?.experience || [];
                  return employmentDetails.length > 0 ? (
                    <div className="profile-section">
                      <h3 className="section-title">{t("jobDetails.viewProfile.sections.workExperience")}</h3>
                      <div className="experience-list">
                        {employmentDetails.map((emp: any, index: number) => {
                          let duration = "";
                          if (!duration && emp.from_period) {
                            try {
                              const getDateValue = (dateObj: any) => {
                                if (!dateObj) return null;
                                if (typeof dateObj === "string") return new Date(dateObj);
                                if (dateObj.$date && typeof dateObj.$date === "object" && dateObj.$date.$numberLong) {
                                  return new Date(parseInt(dateObj.$date.$numberLong));
                                }
                                if (dateObj.$date) return new Date(dateObj.$date);
                                return new Date(dateObj);
                              };

                              const fromDate = getDateValue(emp.from_period);
                              const toDate = emp.currently_working ? null : getDateValue(emp.to_period);

                              const fromDateStr =
                                fromDate && !isNaN(fromDate.getTime())
                                  ? fromDate.toLocaleDateString("en-US", {
                                      year: "numeric",
                                      month: "short",
                                    })
                                  : t("jobDetails.viewProfile.unknown");

                              const toDateStr =
                                toDate && !isNaN(toDate.getTime())
                                  ? toDate.toLocaleDateString("en-US", {
                                      year: "numeric",
                                      month: "short",
                                    })
                                  : t("jobDetails.viewProfile.present");

                              duration = `${fromDateStr} · ${toDateStr}`;
                            } catch (error) {
                              console.error("Error parsing date:", error);
                              duration = t("jobDetails.viewProfile.dateNotAvailable");
                            }
                          }

                          return (
                            <div key={index} className="experience-item">
                              <div className="experience-icon">
                                <i className="fa fa-briefcase"></i>
                              </div>
                              <div className="experience-details">
                                <h4 className="experience-company">{emp?.designation}</h4>
                                <p className="experience-position">{emp?.company_name}</p>
                                <p className="experience-duration">{duration}</p>
                                <div className="experience-description">
                                  {emp?.job_desc ? (
                                    Array.isArray(emp.job_desc) ? (
                                      <ul>
                                        {emp.job_desc.map((desc: string, idx: number) => (
                                          <li key={idx}>{desc}</li>
                                        ))}
                                      </ul>
                                    ) : typeof emp.job_desc === "string" ? (
                                      parse(emp.job_desc)
                                    ) : null
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null;
                })()}

                {/* Education Section */}
                {(() => {
                  const educationDetails = candidateDetails?.education_details || [];
                  return educationDetails?.filter((education: any) => education?.institute_name || education?.degree)
                    ?.length > 0 ? (
                    <div className="profile-section">
                      <h3 className="section-title">{t("jobDetails.viewProfile.sections.education")}</h3>
                      <div
                        className={`education-list ${
                          educationDetails.filter((education: any) => education?.institute_name || education?.degree)
                            .length > 1
                            ? "two-column-layout"
                            : ""
                        }`}
                      >
                        {educationDetails.map((education: any, index: number) =>
                          education?.institute_name || education?.degree ? (
                            <div key={index} className="education-item">
                              <div className="education-icon">
                                <i className="fa fa-graduation-cap"></i>
                              </div>
                              <div className="education-details">
                                <h4 className="education-degree">{education?.institute_name}</h4>
                                <p className="education-institution">{education?.degree}</p>
                                <p className="education-duration">
                                  {education?.completion_year ? "Completion Year: " + education?.completion_year : ""}
                                </p>
                              </div>
                            </div>
                          ) : null,
                        )}
                      </div>
                    </div>
                  ) : null;
                })()}

                {/* Course Section */}
                {candidateDetails?.course_details && candidateDetails.course_details.length > 0 && (
                  <div className="profile-section">
                    <h3 className="section-title">{t("jobDetails.viewProfile.sections.courses")}</h3>
                    <div
                      className={`education-list ${
                        candidateDetails.course_details.length > 1 ? "two-column-layout" : ""
                      }`}
                    >
                      {candidateDetails.course_details.map((course: any, index: number) => (
                        <div key={index} className="education-item">
                          <div className="education-icon">
                            <i className="fa fa-book"></i>
                          </div>
                          <div className="education-details">
                            <h4 className="education-degree">{course.course_name}</h4>
                            <p className="education-institution">{course.institute_name}</p>
                            <p className="education-duration">{course.completion_year}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Projects Section */}
                {candidateDetails?.project_details && candidateDetails.project_details.length > 0 && (
                  <div className="profile-section">
                    <h3 className="section-title">{t("jobDetails.viewProfile.sections.projects")}</h3>
                    <div className="education-list">
                      {candidateDetails.project_details.map((project: any, index: number) => (
                        <div key={index} className="education-item">
                          <div className="education-icon">
                            <i className="fa fa-code"></i>
                          </div>
                          <div className="education-details">
                            <h4 className="education-degree">{project?.project_title}</h4>
                            <p className="education-institution">
                              {project?.organization_name}
                              {project?.project_url ? " - " + project?.project_url : ""}
                            </p>
                            <p className="education-duration">{project?.duration}</p>
                            <div className="education-institution">
                              {project?.project_description ? (
                                typeof project.project_description === "string" ? (
                                  parse(project.project_description)
                                ) : Array.isArray(project.project_description) ? (
                                  <ul>
                                    {project.project_description.map((desc: string, idx: number) => (
                                      <li key={idx}>{desc}</li>
                                    ))}
                                  </ul>
                                ) : null
                              ) : null}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Certificates Section */}
                {candidateDetails?.certificate_details && candidateDetails.certificate_details.length > 0 && (
                  <div className="profile-section">
                    <h3 className="section-title">{t("jobDetails.viewProfile.sections.certificates")}</h3>
                    <div
                      className={`certificates-list ${
                        candidateDetails.certificate_details.length > 1 ? "two-column-layout" : ""
                      }`}
                    >
                      {candidateDetails.certificate_details.map((cert: any, index: number) => (
                        <div key={index} className="certificate-item">
                          <div className="certificate-icon">
                            <i className="fa fa-trophy"></i>
                          </div>
                          <div className="certificate-details">
                            <h4 className="certificate-name">{cert?.certificate_name}</h4>
                            <p className="certificate-issuer">
                              {cert?.institute_name}
                              {cert.completion_location ? " - " + cert.completion_location : ""}
                            </p>
                            {cert?.completion_year}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Personal Details Section */}
                <div className="profile-section personal-details-section">
                  <h3 className="section-title">{t("jobDetails.viewProfile.sections.personalDetails")}</h3>
                  <div className="personal-details-grid">
                    <div className="personal-detail-group">
                      <h4 className="detail-label">{t("jobDetails.viewProfile.fields.currentDesignation")}</h4>
                      <p className="detail-value">
                        {candidateDetails?.basic_details?.cv_headline ||
                          candidateDetails?.employment_details?.[0]?.designation ||
                          "-"}
                      </p>
                    </div>
                    <div className="personal-detail-group">
                      <h4 className="detail-label">{t("jobDetails.viewProfile.fields.currentLocation")}</h4>
                      <p className="detail-value">{candidateDetails?.basic_details?.current_location || "-"}</p>
                    </div>
                    <div className="personal-detail-group">
                      <h4 className="detail-label">{t("jobDetails.viewProfile.fields.languages")}</h4>
                      <p className="detail-value">{candidateDetails?.basic_details?.known_languages || "-"}</p>
                    </div>
                    <div className="personal-detail-group">
                      <h4 className="detail-label">Gender</h4>
                      <p className="detail-value">{candidateDetails?.resolved_labels?.gender?.name || "-"}</p>
                    </div>
                    <div className="personal-detail-group">
                      <h4 className="detail-label">Nationality</h4>
                      <p className="detail-value">{candidateDetails?.resolved_labels?.nationality?.name || "-"}</p>
                    </div>
                    <div className="personal-detail-group">
                      <h4 className="detail-label">Job Type</h4>
                      <p className="detail-value">
                        {candidateDetails?.resolved_labels?.job_types?.length
                          ? candidateDetails.resolved_labels.job_types.map((j: any) => j.name).join(", ")
                          : "-"}
                      </p>
                    </div>
                    <div className="personal-detail-group">
                      <h4 className="detail-label">Workplace</h4>
                      <p className="detail-value">
                        {candidateDetails?.resolved_labels?.workplace_types?.length
                          ? candidateDetails.resolved_labels.workplace_types.map((w: any) => w.name).join(", ")
                          : "-"}
                      </p>
                    </div>
                    <div className="personal-detail-group">
                      <h4 className="detail-label">{t("jobDetails.viewProfile.fields.email")}</h4>
                      <p className="detail-value">{candidateDetails?.jobseeker?.email || "-"}</p>
                    </div>
                    <div className="personal-detail-group">
                      <h4 className="detail-label">{t("jobDetails.viewProfile.fields.mobileNumber")}</h4>
                      <p className="detail-value">{candidateDetails?.jobseeker?.mobile_number || "-"}</p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewProfileModal;
